/**
 * Builder shell: toolbar (template, import, export, layout fields), slot list, stage,
 * inspector and validation issues. Files are the only storage: nothing is persisted
 * locally or on a server.
 */
import { ArrowDown, ArrowUp, Copy, Plus, X } from '@phosphor-icons/react';
import { useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { slotGeometry } from '@ioda/shared';
import { editorReducer, initialState, slotBox } from '../model/editor.ts';
import { exportFileName, importLayoutFile, serializeLayout, validateLayout } from '../model/io.ts';
import { createTemplate, TEMPLATE_IDS, TEMPLATE_NAMES, type TemplateId } from '../model/templates.ts';
import { Field, NumberInput, TextInput } from './fields.tsx';
import { Inspector } from './Inspector.tsx';
import { Stage } from './Stage.tsx';

const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

/** A compact icon-only button; `label` is its accessible name and tooltip. */
function IconButton({ label, onClick, danger = false, children }: { label: string; onClick: () => void; danger?: boolean; children: ReactNode }) {
  return (
    <button type="button" className={`icon-button${danger ? ' danger' : ''}`} aria-label={label} title={label} onClick={onClick}>
      {children}
    </button>
  );
}

export function App() {
  const [state, dispatch] = useReducer(editorReducer, createTemplate('4-box'), initialState);
  const [importIssues, setImportIssues] = useState<{ file: string; issues: string[] } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { layout, selectedSlotId } = state;
  const issues = useMemo(() => validateLayout(layout), [layout]);
  const selectedIndex = layout.slots.findIndex((s) => s.slotId === selectedSlotId);
  const selected = selectedIndex >= 0 ? layout.slots[selectedIndex] : undefined;

  // Unsaved-changes warning: the downloaded file is the only copy.
  useEffect(() => {
    if (!state.dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state.dirty]);

  // Keyboard: nudge, delete, duplicate, undo/redo. Ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch({ type: 'redo' });
        return;
      }
      if (!selected) return;
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        dispatch({ type: 'duplicateSlot', slotId: selected.slotId });
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        dispatch({ type: 'removeSlot', slotId: selected.slotId });
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      const delta: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      const d = delta[e.key];
      if (d) {
        e.preventDefault();
        dispatch({ type: 'updateSlot', slotId: selected.slotId, patch: { positionX: selected.positionX + d[0], positionY: selected.positionY + d[1] } });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const confirmDiscard = () => !state.dirty || window.confirm('Discard unsaved changes to this layout?');

  const loadTemplate = (id: TemplateId) => {
    if (!confirmDiscard()) return;
    setImportIssues(null);
    dispatch({ type: 'load', layout: createTemplate(id) });
  };

  const onImport = async (file: File | undefined) => {
    if (!file) return;
    const result = await importLayoutFile(file);
    if (!result.ok) {
      setImportIssues({ file: file.name, issues: result.issues });
      return;
    }
    if (!confirmDiscard()) return;
    setImportIssues(null);
    dispatch({ type: 'load', layout: result.layout });
  };

  const onExport = () => {
    const text = serializeLayout(layout);
    const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFileName(layout);
    a.click();
    URL.revokeObjectURL(url);
    dispatch({ type: 'markSaved' });
  };

  const layoutIssues = (field: string) => issues.filter((i) => i.path === field).map((i) => i.message);

  return (
    <div className="app">
      <header className="toolbar">
        <h1>IODA Layout Builder</h1>
        {state.dirty && (
          <span className="dirty" title="The downloaded file is the only copy: export before closing">
            Unsaved
          </span>
        )}
        <select
          aria-label="New from template"
          value=""
          onChange={(e) => {
            if (e.target.value) loadTemplate(e.target.value as TemplateId);
          }}
        >
          <option value="">New from template…</option>
          {TEMPLATE_IDS.map((id) => (
            <option key={id} value={id}>
              {id}: {TEMPLATE_NAMES[id]}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => fileInput.current?.click()}>
          Import…
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={(e) => {
            void onImport(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <button type="button" className="primary" disabled={issues.length > 0} onClick={onExport} title={issues.length ? 'Fix the issues first' : undefined}>
          {issues.length ? `Export blocked (${issues.length} issue${issues.length === 1 ? '' : 's'})` : `Export ${exportFileName(layout)}`}
        </button>
        <span className="spacer" />
        <button type="button" title="Undo (Ctrl+Z)" onClick={() => dispatch({ type: 'undo' })} disabled={state.past.length === 0}>
          Undo
        </button>
        <button type="button" title="Redo (Ctrl+Y)" onClick={() => dispatch({ type: 'redo' })} disabled={state.future.length === 0}>
          Redo
        </button>
      </header>

      <aside className="sidebar">
        <section className="layout-fields">
          <Field label="Layout ID (file stem)" issues={layoutIssues('layoutId')} wide>
            <TextInput value={layout.layoutId} placeholder="4-box" onChange={(v) => dispatch({ type: 'updateLayout', patch: { layoutId: v } })} />
          </Field>
          <Field label="Name" issues={layoutIssues('name')} wide>
            <TextInput value={layout.name} onChange={(v) => dispatch({ type: 'updateLayout', patch: { name: v } })} />
          </Field>
          <Field label="Slot count (participants)" issues={layoutIssues('slotCount')} wide>
            <NumberInput value={layout.slotCount} integer min={1} onChange={(v) => dispatch({ type: 'updateLayout', patch: { slotCount: v } })} />
          </Field>
        </section>

        <section className="slot-list">
          <div className="section-head">
            <h2>Slots</h2>
            <button type="button" className="with-icon" onClick={() => dispatch({ type: 'addSlot' })}>
              <Plus size={14} weight="bold" aria-hidden /> Add
            </button>
          </div>
          {layout.slots.length === 0 ? (
            <p className="empty-block">No slots yet. Add one, or start from a template.</p>
          ) : (
            <ul>
              {layout.slots.map((s) => {
                const box = slotGeometry(s).contentBounds;
                return (
                  <li key={s.slotId} className={s.slotId === selectedSlotId ? 'on' : ''}>
                    <button type="button" className="slot-pick" onClick={() => dispatch({ type: 'select', slotId: s.slotId })}>
                      <span className="slot-name">{s.slotId}</span>
                      <span className="slot-meta">
                        {Math.round(box.width)}×{Math.round(box.height)}, z {s.zIndex}
                        {s.hidden ? ', parked' : ''}
                      </span>
                    </button>
                    <span className="slot-actions">
                      <IconButton label={`Raise ${s.slotId}`} onClick={() => dispatch({ type: 'updateSlot', slotId: s.slotId, patch: { zIndex: s.zIndex + 1 } })}>
                        <ArrowUp />
                      </IconButton>
                      <IconButton label={`Lower ${s.slotId}`} onClick={() => dispatch({ type: 'updateSlot', slotId: s.slotId, patch: { zIndex: s.zIndex - 1 } })}>
                        <ArrowDown />
                      </IconButton>
                      <IconButton label={`Duplicate ${s.slotId} (Ctrl+D)`} onClick={() => dispatch({ type: 'duplicateSlot', slotId: s.slotId })}>
                        <Copy />
                      </IconButton>
                      <IconButton label={`Remove ${s.slotId} (Del)`} danger onClick={() => dispatch({ type: 'removeSlot', slotId: s.slotId })}>
                        <X />
                      </IconButton>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={`issues${issues.length ? ' has-issues' : ''}`}>
          <h2>{issues.length ? `${issues.length} issue${issues.length === 1 ? '' : 's'}` : 'Valid layout'}</h2>
          <ul>
            {issues.map((i, n) => (
              <li key={n}>
                <code>{i.path}</code> {i.message}
              </li>
            ))}
          </ul>
        </section>
      </aside>

      <main className="main">
        {importIssues && (
          <div className="import-error" role="alert">
            <strong>Could not import {importIssues.file}. Nothing was changed.</strong>
            <ul>
              {importIssues.issues.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
            <button type="button" onClick={() => setImportIssues(null)}>
              Dismiss
            </button>
          </div>
        )}
        <Stage layout={layout} selectedSlotId={selectedSlotId} dispatch={dispatch} />
      </main>

      <aside className="inspector-pane">
        {selected ? (
          <Inspector key={selected.slotId} slot={selected} index={selectedIndex} issues={issues} dispatch={dispatch} />
        ) : (
          <div className="empty-block">
            <p className="empty-title">No slot selected</p>
            <p>{layout.slots.length === 0 ? 'Add a slot in the list on the left, then edit it here.' : 'Click a box on the canvas, or a slot in the list, to edit it here.'}</p>
          </div>
        )}
        {selected && selected.rotation !== 0 && <p className="note">Rotated slots are edited here; drag handles are off.</p>}
        {selected && selected.boundsType === 'OBS_BOUNDS_NONE' && (
          <p className="note">Without bounds the box is the source size ({Math.round(slotBox(selected).width)}×{Math.round(slotBox(selected).height)}); pick a bounds type to resize.</p>
        )}
      </aside>
    </div>
  );
}
